import ajax from './ajax';
import {
  GEOGRAPHY_LEVELS,
  geographyComboOptions,
  resolveGeographyLevel,
} from '../components/create/geography/geography';
import { configureCountryCombo } from '../components/create/geography/options';

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const resolveDicRef = (value) => {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!/^dic\./i.test(raw)) return value;

  const parts = raw.replace(/^dic\./i, '').split('.').filter(Boolean);
  let cursor = globalThis?.dic;
  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return value;
    if (Object.prototype.hasOwnProperty.call(cursor, part)) {
      cursor = cursor[part];
      continue;
    }
    const fallbackKey = Object.keys(cursor).find(
      (key) => String(key).toLowerCase() === part.toLowerCase()
    );
    if (!fallbackKey) return value;
    cursor = cursor[fallbackKey];
  }

  return cursor ?? value;
};

const resolveKey = (options = {}) => {
  const candidate = String(options.configKey ?? options.key ?? '').trim();
  return !candidate || candidate.startsWith('dic.') ? 'main' : candidate;
};

const serviceTarget = (options = {}) => ({
  product: options.product,
  category: options.category,
  domain: options.domain,
  module: options.module,
});

const recordValues = (record = {}) => Object.fromEntries(
  Object.entries(record || {}).filter(([key, value]) => (
    key !== 'permissions'
    && !String(key).startsWith('__')
    && (value === null || ['string', 'number', 'boolean'].includes(typeof value))
  ))
);

const recordData = (record = {}) => Object.entries(recordValues(record))
  .map(([name, value]) => ({ name, value }));

const errorMessage = (response = null) => (
  response?.error
  || response?.message
  || globalThis?.dic?.message?.errorSave
  || globalThis?.dic?.labels?.error
  || 'SAVE_FAILED'
);

const relationChildAlias = (relation = null) => String(relation?.child || '')
  .trim()
  .split('.')
  .pop();

const applyRelationParentValue = (record = {}, { options = {}, context = {}, component = null } = {}) => {
  if (!record || typeof record !== 'object') return record;

  const componentOptions = component?.options && typeof component.options === 'object'
    ? component.options
    : {};
  const relation = componentOptions.relation || context?.relation || options?.relation;
  const childAlias = relationChildAlias(relation);
  const parentValue = componentOptions.relationParentValue
    ?? context?.relationParentValue
    ?? options?.relationParentValue;

  if (!childAlias || !hasValue(parentValue)) return record;
  record[childAlias] = parentValue;
  return record;
};

const saveRemoteRecord = async (options, record, context = {}) => {
  const scopedRecord = applyRelationParentValue({ ...record }, { options, context });
  const response = await ajax.post({
    ...serviceTarget(options),
    method: 'save',
    body: {
      key: resolveKey(options),
      data: recordData(scopedRecord),
    },
    config: { loader: false },
  });

  if (response?.status !== 'success') {
    return { accepted: false, message: errorMessage(response) };
  }

  const saved = Array.isArray(response?.data) ? response.data[0] : null;
  return {
    accepted: true,
    record: saved && typeof saved === 'object' ? { ...scopedRecord, ...saved } : { ...scopedRecord },
  };
};

const normalizeFieldList = (value) => (
  (Array.isArray(value) ? value : [value])
    .map((name) => String(name || '').trim())
    .filter(Boolean)
);

const columnAlias = (column = {}) => String(
  column?.alias
  || column?.id?.split?.('.').pop?.()
  || column?.name
  || ''
).trim();

const resolveConfiguredGeographyField = (value) => {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return String(value.name || value.id || value.alias || '').trim();
};

const resolveGeographyFieldMap = ({ component = null, editable = {}, draft = {} } = {}) => {
  const explicit = editable.fields && typeof editable.fields === 'object' && !Array.isArray(editable.fields)
    ? editable.fields
    : (
      editable.geographyFields && typeof editable.geographyFields === 'object' && !Array.isArray(editable.geographyFields)
        ? editable.geographyFields
        : {}
    );
  const group = String(editable.group || editable.geographyGroup || 'geography').trim() || 'geography';
  const columns = Array.isArray(component?.lastHeader) ? component.lastHeader : [];
  const map = {};

  for (const level of Object.keys(GEOGRAPHY_LEVELS)) {
    const configured = resolveConfiguredGeographyField(explicit[level]);
    if (configured) {
      map[level] = configured;
      continue;
    }

    const groupedColumn = columns.find((candidate) => {
      const candidateEditable = candidate?.editable && typeof candidate.editable === 'object'
        ? candidate.editable
        : {};
      const candidateGroup = String(
        candidateEditable.group
        || candidateEditable.geographyGroup
        || 'geography'
      ).trim() || 'geography';
      if (candidateGroup !== group) return false;
      const candidateLevel = candidateEditable.level
        || candidateEditable.geographyLevel
        || resolveGeographyLevel(columnAlias(candidate));
      return candidateLevel === level;
    });

    const groupedAlias = columnAlias(groupedColumn);
    if (groupedAlias) {
      map[level] = groupedAlias;
      continue;
    }

    const recordField = Object.keys(draft || {}).find((key) => resolveGeographyLevel(key) === level);
    if (recordField) map[level] = recordField;
  }

  return { group, map };
};

const refreshRowRemoteComboDependents = (host, changedField) => {
  const row = host?.closest?.('tr');
  if (!row || !changedField) return;

  const geographyGroup = String(host?.dataset?.geographyGroup || '').trim();

  row.querySelectorAll('.dg-inline-remote-combo').forEach((candidate) => {
    if (candidate === host) return;
    if (
      geographyGroup
      && String(candidate?.dataset?.geographyGroup || '').trim() !== geographyGroup
    ) {
      return;
    }
    candidate.__refreshRemoteComboDependency?.(changedField);
  });
};

const createRemoteComboEditor = async (
  { record = {}, column = {}, component = null } = {},
  overrides = {}
) => {
  const baseEditable = column?.editable && typeof column.editable === 'object'
    ? column.editable
    : {};
  const editable = { ...baseEditable, ...overrides };
  const alias = String(overrides.alias || columnAlias(column)).trim();
  const host = document.createElement('div');
  host.className = [
    'dg-inline-remote-combo',
    editable.geographyLevel ? 'dg-inline-geography' : '',
  ].filter(Boolean).join(' ');

  const geographyGroup = String(editable.geographyGroup || '').trim();
  if (geographyGroup) host.dataset.geographyGroup = geographyGroup;
  if (editable.geographyLevel) host.dataset.geographyLevel = editable.geographyLevel;

  if (!alias) return host;

  // Em criação inline o DataGrid mantém o registro em inlineDraft; em edição
  // explícita usa inlineEditDraft. O editor precisa escrever no mesmo objeto que
  // será confirmado pela linha, e não em uma cópia transitória vazia.
  const draft = component?.inlineEditDraft || component?.inlineDraft || record;
  applyRelationParentValue(draft, { component });
  const dependencies = normalizeFieldList(editable.dependsOn);
  const clears = normalizeFieldList(editable.clears);

  if (!hasValue(draft[alias]) && hasValue(editable.defaultValue)) {
    draft[alias] = resolveDicRef(editable.defaultValue);
  }

  const values = () => {
    const base = { ...recordValues(draft) };
    if (typeof editable.values === 'function') {
      return { ...base, ...(editable.values(draft) || {}) };
    }
    if (editable.values && typeof editable.values === 'object' && !Array.isArray(editable.values)) {
      return { ...base, ...editable.values };
    }
    return base;
  };
  let refreshChain = Promise.resolve();

  // O editor customizado é criado antes de ser anexado à <tr>. Portanto não
  // registramos listeners procurando a linha durante a criação. Em vez disso,
  // cada host expõe seu refresh e o combo alterado resolve a própria linha no
  // momento real da mudança, quando todos os editores já estão montados.
  host.__refreshRemoteComboDependency = (changedField) => {
    if (!dependencies.includes(String(changedField || ''))) return refreshChain;

    refreshChain = refreshChain.then(async () => {
      const combo = host.querySelector('.combo')?.combo;
      if (!combo?.fill) return;

      const ready = dependencies.every((field) => hasValue(draft[field]));
      if (!ready) draft[alias] = null;

      const previousValue = combo.hidden?.value ?? '';
      const preferredValue = ready && hasValue(draft[alias]) ? draft[alias] : null;

      // fill() preserva o hidden atual quando recebe valor vazio. Para um
      // cascade isso seria incorreto: após trocar país/estado o valor antigo
      // não pode sobreviver ao novo conjunto de opções.
      if (combo.hidden) combo.hidden.value = '';
      if (combo.options && typeof combo.options === 'object') {
        combo.options.value = null;
      }

      await combo.fill({
        values: values(),
        value: preferredValue,
      });

      const nextValue = combo.hidden?.value ?? '';
      draft[alias] = hasValue(nextValue) ? nextValue : null;

      // Se o refill limpou este campo, dependentes indiretos também precisam
      // ser atualizados, por exemplo país -> estado -> cidade.
      if (String(previousValue) !== String(nextValue)) {
        refreshRowRemoteComboDependents(host, alias);
      }
    });

    return refreshChain;
  };

  const comboOptions = {
    element: 'combo',
    parent: host,
    id: `dg-inline-${alias}-${Math.random().toString(36).slice(2, 9)}`,
    name: alias,
    label: false,
    full: true,
    value: draft[alias] ?? '',
    filter: editable.filter === true,
    loader: editable.loader !== false,
    disabled: editable.lockOnEdit === true && hasValue(draft.id),
    size: editable.size,
    scope: editable.scope,
    product: editable.product,
    service: editable.service,
    category: editable.category,
    domain: editable.domain,
    module: editable.module,
    key: editable.key,
    // Fonte local de opções (ex.: Geography resolve países/estados/cidades no
    // próprio EP). Sem repassá-la o Combo inline ficava sem fonte e vazio.
    options: editable.options,
    values: values(),
    data: editable.geographyLevel
      ? {
        geographyGroup,
        geographyLevel: editable.geographyLevel,
      }
      : undefined,
    change: (event) => {
      const hidden = event?.currentTarget?.querySelector?.('input[type="hidden"]');
      draft[alias] = hidden?.value ?? '';
      for (const field of clears) draft[field] = null;

      host.dispatchEvent(new CustomEvent('datagrid:inline-field-change', {
        bubbles: true,
        detail: {
          field: alias,
          geographyGroup: geographyGroup || null,
          geographyLevel: editable.geographyLevel || null,
        },
      }));
      refreshRowRemoteComboDependents(host, alias);
    },
  };

  Object.keys(comboOptions).forEach((key) => {
    if (comboOptions[key] === undefined || comboOptions[key] === null || comboOptions[key] === '') {
      delete comboOptions[key];
    }
  });

  await globalThis.create(comboOptions);

  if (editable.geographyLevel === 'country') {
    const combo = host.querySelector('.combo')?.combo;
    const configured = configureCountryCombo(combo, {
      accept: editable.accept,
      reject: editable.reject,
      continentGroup: editable.continentGroup,
      readOnly: editable.readOnly ?? editable.readonly,
      disabled: comboOptions.disabled,
    });

    if (hasValue(configured.value)) {
      draft[alias] = configured.value;
    }
  }

  return host;
};

const createGeographyEditor = async ({ record = {}, column = {}, component = null } = {}) => {
  const editable = column?.editable && typeof column.editable === 'object'
    ? column.editable
    : {};
  const alias = columnAlias(column);
  const draft = component?.inlineEditDraft || component?.inlineDraft || record;
  const level = String(
    editable.level
    || editable.geographyLevel
    || resolveGeographyLevel(alias)
    || ''
  ).trim();

  if (!GEOGRAPHY_LEVELS[level]) {
    const host = document.createElement('div');
    host.className = 'dg-inline-remote-combo dg-inline-geography';
    console.error(`[datagrid.geography] nível não resolvido para a coluna "${alias}".`);
    return host;
  }

  const { group, map } = resolveGeographyFieldMap({ component, editable, draft });
  map[level] = map[level] || alias;

  const levelConfig = GEOGRAPHY_LEVELS[level];
  const dependencies = levelConfig.dependsOn
    .map((dependencyLevel) => map[dependencyLevel])
    .filter(Boolean);
  const clears = levelConfig.clears
    .map((clearLevel) => map[clearLevel])
    .filter(Boolean);

  const canonicalValues = () => ({
    country: map.country ? draft[map.country] : undefined,
    state: map.state ? draft[map.state] : undefined,
    city: map.city ? draft[map.city] : undefined,
  });

  const shared = geographyComboOptions(level, {
    filter: editable.filter ?? true,
    size: editable.size ?? 10,
    full: true,
  });
  delete shared.element;

  return createRemoteComboEditor(
    { record, column, component },
    {
      ...shared,
      alias,
      defaultValue: editable.defaultValue,
      dependsOn: editable.dependsOn ?? dependencies,
      clears: editable.clears ?? clears,
      values: canonicalValues,
      geographyGroup: group,
      geographyLevel: level,
    }
  );
};

export const createDatagridCallbackRegistry = (options = {}) => ({
  'datagrid.remoteSave': async ({ record = {}, context = {} } = {}) => saveRemoteRecord(options, record, context),
  'datagrid.remoteCreate': async ({ record = {}, context = {} } = {}) => saveRemoteRecord(options, record, context),
  'datagrid.remoteReorder': async ({ rows = [], changes = [], context = {} } = {}) => {
    for (const change of changes) {
      const keys = change?.keys && typeof change.keys === 'object' ? change.keys : {};
      const row = rows.find((candidate) => Object.entries(keys).every(([key, value]) => candidate?.[key] === value));
      if (!row) continue;
      const result = await saveRemoteRecord(options, row, context);
      if (result?.accepted === false) return result;
    }
    return { accepted: true };
  },
  'datagrid.remoteCombo': createRemoteComboEditor,
  'datagrid.geography': createGeographyEditor,
});

export default createDatagridCallbackRegistry;
